import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { SkillType } from '@/services/tasks/skillSettings.types';
import { FC, FunctionComponent } from 'react';
import { FiEdit } from 'react-icons/fi';
import strings from '../admin.translations';

const columnWidths = {
  edit: '16%'
};
const pageSize = 20;

const SkillsTableHeader: FC = () => (
  <thead>
    <tr>
      <th className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std">
        {strings.skillsSection.table.name}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.edit }}
      >
        {strings.skillsSection.table.edit}
      </th>
    </tr>
  </thead>
);

interface SkillsTableRowProps {
  skill: SkillType;
  index: number;
  onEdit: (skillId: string) => void;
}

const AdminSkillsTableRow: FC<SkillsTableRowProps> = ({ skill, index, onEdit }) => {
  const alternateRowStyling = index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std';
  const disabledRowStyling = 'bg-gray-300';

  return (
    <tr className={skill.isActive ? alternateRowStyling : disabledRowStyling}>
      <td className="px-4 py-4 text-sm font-medium text-primary-std sm:px-6">
        <div className={`flex h-[2.2rem] ${'flex-col justify-center'}`}>
          <div>
            <p className="font-semibold">{skill.name}</p>
            <p>{skill.displayName}</p>
            {!skill.isActive && <p>{strings.tasksSection.table.disabled}</p>}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.edit }}>
        <div className="flex justify-center">
          <button
            type="button"
            aria-label={`${strings.skillsSection.table.edit} ${skill.name}`}
            className="text-secondary-std hover:text-primary-std"
            onClick={() => onEdit(skill.id)}
          >
            <FiEdit className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

type AdminSkillsTableProps = {
  skills: SkillType[];
  onEdit: (skillId: string) => void;
};

const AdminSkillsTable: FunctionComponent<AdminSkillsTableProps> = ({ skills, onEdit }) => {
  const [offset, limit, controls] = usePagination(pageSize, skills.length);
  const paginatedSkills = skills.slice(offset, offset + limit);

  return (
    <div className="w-full overflow-hidden">
      <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
        <SkillsTableHeader />
        <tbody className="divide-y divide-primary-std">
          {paginatedSkills.map((skill, index) => {
            return <AdminSkillsTableRow key={skill.id} skill={skill} index={index} onEdit={onEdit} />;
          })}
        </tbody>
      </table>
      {skills.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};

export default AdminSkillsTable;
